import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useRoleContext } from '../../../contexts/RoleProvider';
import useRolesActions from '../../../hooks/APIHooks/users/useRolesActions';
import { Role } from '../../../types/roles';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import ConsentModal from '../../CommonComps/Modals/ConsentModal';
import CreateEditRoleModal from '../../CommonComps/Modals/CreateEditRoleModal';

const RoleMutationModalsContainer = () => {
  // integration of react hooks here
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // integration of context hooks here
  const {
    targetedRole,
    showDeleteModal,
    showEditModal,
    setTargetedRole,
    setShowDeleteModal,
    setShowEditModal,
  } = useRoleContext();

  // integration of custom API hooks here
  const { deleteRoleHandler } = useRolesActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to handle role deleting by id feature declared here
  const deleteRoleByIdHandler = async () => {
    try {
      setIsDeleting(true);

      await deleteRoleHandler
        .mutateAsync({ roleId: targetedRole?.id as number })
        .then((res) => {
          if (res.status === 204) {
            toast.success('Role Deleted!!');

            setShowDeleteModal(false);
            setTargetedRole(null);
            queryClient.invalidateQueries({ queryKey: ['role-list'] });
          } else {
            toast.error(res.data.message || 'Role Deletion Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('something Went Wrong!!');
    } finally {
      setIsDeleting(false);
    }
  };

  // rendering role mutation modal container component here
  return (
    <section>
      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeleteModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to delete this{' '}
                <span className='text-base-light-blue font-semibold'>
                  ({targetedRole?.role_name})
                </span>{' '}
                role? Once you delete it, you can not recover it.
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deleteRoleByIdHandler}
            handleModalClose={() => setShowDeleteModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showEditModal && (
          <CreateEditRoleModal
            title='update'
            role={targetedRole as Role}
            handleModalClose={() => setShowEditModal(false)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default RoleMutationModalsContainer;
